import { useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { PlayingCard as PlayingCardType } from '@/types'
import { Hand } from './Hand'
import { isBlackjack, isBusted } from '../utils/blackjackLogic'
import type { GameResult } from '../utils/blackjackLogic'
import type { DealingTarget } from '../hooks/useBlackjackGame'

interface BlackjackTableProps {
  playerHand: PlayingCardType[]
  dealerHand: PlayingCardType[]
  playerValue: number
  dealerValue: number
  showDealerCard: boolean
  result: GameResult | null
  dealingTo?: DealingTarget
}

export function BlackjackTable({
  playerHand,
  dealerHand,
  playerValue,
  dealerValue,
  showDealerCard,
  result,
  dealingTo,
}: BlackjackTableProps) {
  const tableRef = useRef<HTMLDivElement>(null)
  const playerWon = result === 'win' || result === 'blackjack'
  const playerBusted = result === 'bust'
  const playerHasBlackjack = isBlackjack(playerHand)
  const dealerBusted = showDealerCard && isBusted(dealerHand)
  const dealerHasBlackjack = showDealerCard && isBlackjack(dealerHand)

  return (
    <div className="relative flex-1 flex flex-col" ref={tableRef}>
      {/* Table surface */}
      <div
        className="flex-1 rounded-t-[200px] rounded-b-xl mx-4 my-4 p-6 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at center, #0d5c0d 0%, #0a4a0a 50%, #073807 100%)',
          boxShadow: 'inset 0 0 100px rgba(0,0,0,0.4), 0 10px 30px rgba(0,0,0,0.5)',
          border: '8px solid #2a1810',
        }}
      >
        {/* Felt texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Decorative gold border */}
        <div className="absolute top-4 left-4 right-4 bottom-4 border border-casino-gold/20 rounded-t-[180px] rounded-b-lg pointer-events-none" />

        {/* Table title - debajo del mazo */}
        <div className="absolute top-[88px] left-1/2 -translate-x-1/2 pointer-events-none">
          <span className="font-cinzel text-casino-gold/40 text-xs tracking-[0.2em]">
            BLACKJACK PAYS 3 TO 2
          </span>
        </div>

        {/* Insurance line */}
        <div className="absolute top-[105px] left-1/2 -translate-x-1/2 pointer-events-none">
          <span className="font-cinzel text-casino-gold/25 text-[10px] tracking-[0.15em]">
            INSURANCE PAYS 2 TO 1
          </span>
        </div>

        {/* Card deck visual - CENTRO SUPERIOR con mano del dealer */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          {/* Deck stack effect */}
          <div className="relative">
            <div className="absolute top-1 left-1 w-12 h-[68px] rounded-lg bg-gray-800/60" />
            <div className="absolute top-0.5 left-0.5 w-12 h-[68px] rounded-lg bg-gray-700/80" />
            <motion.div
              animate={dealingTo ? { scale: [1, 0.97, 1] } : {}}
              transition={{ duration: 0.4 }}
              className="relative w-12 h-[68px] rounded-lg shadow-lg overflow-hidden border-2 border-casino-gold/60"
              style={{
                boxShadow: '2px 2px 0 #333, 4px 4px 0 #222, 6px 6px 8px rgba(0,0,0,0.5)',
              }}
            >
              <img
                src="/img/card-back.png"
                alt="Deck"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
          {/* Dealer hand emoji - acompaña la carta */}
          <motion.div
            animate={dealingTo ? {
              y: [0, dealingTo === 'dealer' ? 60 : 180, 0],
              x: [0, 20, 0],
              rotate: [0, -25, 0],
              scale: [1, 1.15, 1]
            } : {}}
            transition={{
              duration: 0.8,
              ease: "easeInOut"
            }}
            className="text-3xl filter drop-shadow-lg"
            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
          >
            🤚
          </motion.div>
        </div>

        {/* Dealing Card Animation - card flies from center deck to destination */}
        <AnimatePresence>
          {dealingTo && (
            <motion.div
              initial={{
                top: 16,
                left: '50%',
                x: '-50%',
                rotate: -5,
                scale: 0.8,
                opacity: 0,
              }}
              animate={{
                top: dealingTo === 'dealer' ? '28%' : '68%',
                left: '50%',
                x: '-50%',
                rotate: dealingTo === 'dealer' ? 3 : -3,
                scale: 1,
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
                transition: { duration: 0.2 }
              }}
              transition={{
                duration: 0.7,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="absolute z-30 pointer-events-none"
              style={{ transformOrigin: 'center center' }}
            >
              {/* Flying card */}
              <motion.div
                initial={{ rotateY: 0, rotateZ: -10 }}
                animate={{ rotateY: [0, 10, 0], rotateZ: [-10, 5, 0] }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-12 h-[68px] rounded-lg overflow-hidden shadow-2xl border-2 border-casino-gold/60"
                style={{
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5), 0 5px 15px rgba(212,175,55,0.2)',
                }}
              >
                <img
                  src="/img/card-back.png"
                  alt="Card"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hands area */}
        <div className="flex flex-col justify-around items-center h-full pt-24 pb-4">
          {/* Dealer hand */}
          <Hand
            cards={dealerHand}
            value={dealerValue}
            label="DEALER"
            isDealer
            showHiddenCard={showDealerCard}
            isWinner={result === 'lose' && !playerBusted}
            isBusted={dealerBusted}
            isBlackjack={dealerHasBlackjack}
          />

          {/* Divider */}
          <div className="w-64 h-px bg-gradient-to-r from-transparent via-casino-gold/30 to-transparent" />

          {/* Player hand */}
          <Hand
            cards={playerHand}
            value={playerValue}
            label="YOUR HAND"
            isWinner={playerWon}
            isBusted={playerBusted}
            isBlackjack={playerHasBlackjack}
          />
        </div>
      </div>
    </div>
  )
}
